import type { ICellRendererParams } from 'ag-grid-enterprise';
import { transparentize } from 'polished';
import { memo } from 'react';
import type { DefaultTheme } from 'styled-components';
import { ModeEnum } from '../../../types';
import { HStack } from '../../Core';
import { Icon, IconName } from '../../Icons';
import { IndicatorBadge } from '../../IndicatorBadge';
import { Text } from '../../Text';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

export type ModeStatusColumnParams = {
  /** Get the ModeEnum value from the cell params */
  getModeValue?: (params: ICellRendererParams) => ModeEnum;
};

type ModeStatusBadgeProps = {
  modeValue: ModeEnum;
  theme: DefaultTheme;
};

const ModeStatusBadge = memo<ModeStatusBadgeProps>(({ modeValue, theme }) => {
  const isEnabled = modeValue === ModeEnum.Enabled;

  const background = isEnabled ? theme.backgroundPrimaryButton : transparentize(0.92, theme.colors.gray['100']);
  const iconColor = isEnabled ? theme.colorTextPrimaryButton : theme.colors.gray['090'];
  const textColor = isEnabled ? theme.colorTextPrimaryButton : theme.colors.gray['090'];
  const icon = isEnabled ? IconName.CheckCheckbox : IconName.Close;

  return (
    <IndicatorBadge
      background={background}
      children={
        <HStack gap="spacing4" p="spacing2">
          <Icon icon={icon} color={iconColor} />
          <Text color={textColor} fontSize="fontSizeSmall">
            {modeValue}
          </Text>
        </HStack>
      }
    />
  );
});

/*
 * This column is used to display mode status (Enabled/Disabled) with icon and badge
 */
export const modeStatus: ColDefFactory<Column<ModeStatusColumnParams>> = column => ({
  ...baseColumn(column),
  cellRenderer: (params: ICellRendererParams<unknown, ModeEnum>) => {
    const modeValue = column.params?.getModeValue
      ? column.params.getModeValue(params)
      : (params.value ?? ModeEnum.Disabled);

    const theme = params.context.current.theme!;

    return <ModeStatusBadge modeValue={modeValue} theme={theme} />;
  },
});
